import React from "react";
import { useHistory } from "react-router";
import { ICustomerVM } from "../../vm";
import { ToastContext } from "../common/ToastProvider";
import { getSlaMatrixAlertDetails } from "../../services/dashboard/DashboardService";
import { Loading } from "../../services/Loading";
import {
  DatePicker,
  DayOfWeek,
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  DetailsRow,
  Dropdown,
  Fabric,
  IColumn,
  IDetailsListProps,
  IDetailsRowStyles,
  IDropdownOption,
  SelectionMode,
  Stack,
  Text,
} from "office-ui-fabric-react";
import { childrenGap, childrenGap8 } from "../common/Constants";
import PageTitle from "../common/PageTitle";
import { ReactComponent as CustomerIcon } from "../../images/customer.svg";
import NoDataTile from "../common/NoDataTile";
import moment from "moment";
import { getAllDataCenters } from "../../services/datacenter/DatacenterService";

export interface SlaMatrixAlertDetailsProps { }

const SlaMatrixAlertDetails: React.FC<SlaMatrixAlertDetailsProps> = () => {
  // custom hooks
  // const params: any = useParams();
  const history = useHistory();
  const { showToast } = React.useContext(ToastContext);

  const queryParams = new URLSearchParams(window.location.search);

  // state
  const [state, setState] = React.useState({
    isLoading: false,
    slaMatrixAlertDetails: [],
    searchObj: {
      date: queryParams.get("date") ? queryParams.get("date") : new Date(),
      datacenterIds: queryParams.get("datacenterId")
        ? [queryParams.get("datacenterId")]
        : [],
      type: queryParams.get("type") ? queryParams.get("type") : "",
    },
    datacenterOptions: [],
    selectedDatacenter: queryParams.get("datacenterId")
      ? queryParams.get("datacenterId")
      : "",
  } as {
    isLoading: boolean;
    slaMatrixAlertDetails: ICustomerVM[];
    searchObj: {
      date: string;
      datacenterIds: string[];
      type: string;
    };
    datacenterOptions: IDropdownOption[];
    selectedDatacenter: string;
  });

  React.useEffect(() => {
    const asyncFunc = async () => {
      // await getSlaMatrixDetails();
      await getDatacenters();
    };
    asyncFunc();
  }, []);

  const getDatacenters = async () => {
    setState({ ...state, isLoading: true });
    let result = await getAllDataCenters();
    if (result?.success) {
      let datacenterOptions: IDropdownOption[] = [];
      result.data.forEach((d) => {
        datacenterOptions.push({
          key: d._id as string,
          text: d.name,
        });
      });
      let obj = {
        ...state,
        datacenterOptions,
      };
      await getSlaMatrixDetails(obj);
    } else {
      setState({ ...state, isLoading: false });
      showToast(result?.message || "Error while getting datacenters", "error");
    }
  };

  const getSlaMatrixDetails = async (paramState: any) => {
    let localState = { ...state };
    if (paramState) {
      localState = { ...paramState };
    }
    setState({ ...localState, isLoading: true });

    let result = await getSlaMatrixAlertDetails(localState.searchObj);
    if (result?.success) {
      setState({
        ...localState,
        slaMatrixAlertDetails: getDetailListData(result.data),
        isLoading: false,
      });
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result?.message || "Error while getting sla matrix alert details",
        "error"
      );
    }
  };

  const getDetailListData = (data: ICustomerVM[]) => {
    const dataArr = data.map((d) => {
      if (d.location)
        d["customerLocation"] = d.location.map(
          (l) =>
            l?.datacenter?.name + (l?.circuitId ? " (" + l.circuitId + ")" : "")
        );
      return d;
    });
    return dataArr;
  };

  const _renderItemColumn = (item?: any, index?: number, column?: IColumn) => {
    switch (column?.key) {
      case "actions":
        return (
          <DefaultButton
            text="Check Readings"
            onClick={() => {
              history.push(
                `/customers/${item._id}/temperature-humidity?date=${state.searchObj.date}`
              );
            }}
          />
        );
      case "customerLocation":
        return (
          <Stack horizontal wrap tokens={childrenGap8}>
            {item.customerLocation && item.customerLocation.length > 0
              ? item.customerLocation.map((i: any, index: number) => (
                <Stack.Item className="ms-DetailsRow-cell">
                  {i +
                    (item.customerLocation.length - 1 !== index ? ", " : "")}
                </Stack.Item>
              ))
              : "NA"}
          </Stack>
        );
      case "name":
        return (
          <Text styles={{ root: { fontWeight: 600 } }}>
            {item[column.fieldName as any]}
          </Text>
        );
      default:
        return <span>{item[column?.fieldName as any]}</span>;
    }
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading isModeless={true} />}
      <Stack tokens={childrenGap}>
        <Stack.Item>
          <Stack horizontal horizontalAlign="space-between">
            <Stack.Item>
              <PageTitle
                icon={CustomerIcon}
                title="Customers"
                description="List of all customers"
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack horizontal wrap tokens={childrenGap}>
            <Stack.Item>
              <Dropdown
                selectedKey={state.selectedDatacenter}
                styles={{ dropdown: { width: 200 } }}
                options={state.datacenterOptions}
                onChange={async (e, v: IDropdownOption | undefined) => {
                  if (v) {
                    let datacenterIds: string[] = [];
                    datacenterIds.push(v.key as string);
                    let obj = {
                      ...state,
                      selectedDatacenter: v.key as string,
                      searchObj: {
                        ...state.searchObj,
                        datacenterIds,
                      },
                    };
                    // setState();
                    await getSlaMatrixDetails(obj as any);
                  }
                }}
              />
            </Stack.Item>
            <Stack.Item>
              <DatePicker
                firstDayOfWeek={DayOfWeek.Sunday}
                showMonthPickerAsOverlay={true}
                value={new Date(state.searchObj.date)}
                showGoToToday={false}
                onSelectDate={async (date: Date | null | undefined) => {
                  let obj = {
                    ...state,
                    searchObj: {
                      ...state.searchObj,
                      date: moment(date).format("YYYY-MM-DD"),
                    },
                  };
                  await getSlaMatrixDetails(obj as any);
                }}
                styles={{ root: { width: 200 } }}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <div>
            {state.slaMatrixAlertDetails.length > 0 ? (
              <Fabric>
                <DetailsList
                  items={state.slaMatrixAlertDetails}
                  columns={[
                    {
                      key: "name",
                      name: "Name",
                      fieldName: "name",
                      minWidth: 200,
                      maxWidth: 450,
                      isResizable: true,
                    },
                    {
                      key: "regSapNo",
                      name: "Registered SAP Code",
                      fieldName: "regSapNo",
                      minWidth: 150,
                      maxWidth: 250,
                      isResizable: true,
                    },
                    {
                      key: "customerLocation",
                      name: "Location",
                      fieldName: "customerLocation",
                      minWidth: 350,
                      maxWidth: 600,
                      isResizable: true,
                    },
                    {
                      key: "actions",
                      name: "Actions",
                      fieldName: "actions",
                      minWidth: 130,
                      maxWidth: 180,
                      isResizable: true,
                    },
                  ]}
                  compact={true}
                  setKey="set"
                  onRenderItemColumn={_renderItemColumn}
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                  onRenderRow={_onRenderRow}
                />
              </Fabric>
            ) : (
              <NoDataTile title="No Customers Found"></NoDataTile>
            )}
          </div>
        </Stack.Item>
      </Stack>
    </React.Fragment>
  );
};

export default SlaMatrixAlertDetails;
